import {
  GetEventRegistrationStatusRequest,
  GetEventRegistrationStatusOptions,
  GetEventRegistrationStatusResponse,
  UpsertPersonRequest,
  UpsertPersonResponse,
  UpsertPersonOptions,
  IdentifyRequest,
  IdentifyResponse,
  BrowserIdentifyOptions,
  RegisterForEventRequest,
  RegisterForEventResponse,
  BrowserRegisterForEventOptions,
  SubscribeToEmailRequest,
  SubscribeToEmailResponse,
  BrowserSubscribeToEmailOptions,
  ApiClient,
} from "@quotientjs/core"
import type { BrowserContextManager } from "@/clients/browser-context"
import type { Store } from "@/clients/store"

export class People {
  private apiClient: ApiClient
  private store: Store
  private browserContext: BrowserContextManager

  constructor(
    apiClient: ApiClient,
    store: Store,
    browserContext: BrowserContextManager,
  ) {
    this.apiClient = apiClient
    this.store = store
    this.browserContext = browserContext
  }

  async upsert(options: UpsertPersonOptions): Promise<UpsertPersonResponse> {
    const request = new UpsertPersonRequest(options)
    const response = await this.apiClient.call(request)
    this.store.setPersonId(response.personId)
    return response
  }

  /**
   * Identifies a person by email, creating them if they don't already exist.
   * Attribution is implicit here: the current browser context (device,
   * session, and touch) is attached automatically, the same way a tracked
   * event would carry it.
   */
  async identify(options: BrowserIdentifyOptions): Promise<IdentifyResponse> {
    const browserContext = this.browserContext.prepare()
    const request = new IdentifyRequest({ ...options, browserContext })
    const response = await this.apiClient.call(request)
    this.store.setPersonId(response.personId)
    return response
  }

  /**
   * Identifies a person by email and registers them for `eventSlug`.
   * Attribution is implicit here: the current browser context (device,
   * session, and touch) is attached automatically, the same way `identify()`
   * does. Emits marketingEventRegistration automatically; no analytics.event()
   * call is needed.
   */
  async registerForEvent(
    options: BrowserRegisterForEventOptions,
  ): Promise<RegisterForEventResponse> {
    const browserContext = this.browserContext.prepare()
    const request = new RegisterForEventRequest({ ...options, browserContext })
    const response = await this.apiClient.call(request)
    this.store.setPersonId(response.personId)
    return response
  }

  /**
   * Identifies a person by email and subscribes them if eligible. Attribution
   * is implicit here: the current browser context (device, session, and
   * touch) is attached automatically, the same way `identify()` does. Double
   * opt-in sets new subscriptions to PENDING instead of SUBSCRIBED.
   */
  async subscribeToEmail(
    options: BrowserSubscribeToEmailOptions,
  ): Promise<SubscribeToEmailResponse> {
    const browserContext = this.browserContext.prepare()
    const request = new SubscribeToEmailRequest({ ...options, browserContext })
    const response = await this.apiClient.call(request)
    this.store.setPersonId(response.personId)
    return response
  }
  /** Reads provider confirmation without changing the browser's identified person. */
  async getEventRegistrationStatus(
    options: GetEventRegistrationStatusOptions,
  ): Promise<GetEventRegistrationStatusResponse> {
    return await this.apiClient.call(
      new GetEventRegistrationStatusRequest(options),
    )
  }
}
